import React, { useCallback, useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import {
  Typography,
  Box,
  Paper,
  Chip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import { Alert } from '@material-ui/lab'
import { CloudUploadOutlined as CloudUploadIcon, Check as CheckIcon, PriorityHigh as WarnIcon, InfoOutlined as InfoIcon } from '@material-ui/icons'
import { Toast } from 'components/shared/Toaster'
import Loader from 'components/shared/Loader'
import Button from 'components/shared/Button'
import ReportEmptyState from 'components/shared/ReportEmptyState'
import strings from 'strings'
import IncomeStatementComparison from './IncomeStatementComparison'
import {
  ACCENT_PURPLE,
  BANNER_BG,
  CHECK_STATUS,
  SEVERITY_STYLES,
  canonicalLine,
  deriveChecks,
  deriveChecksChip,
  deriveCostStructure,
  deriveFlags,
  deriveKeyIndicators,
  deriveSummary,
  formatCurrency,
  formatOptionalCurrency,
  formatOptionalShare,
  formatPeriodRange,
  formatShare,
  getStatusStyle,
  readMetrics,
  rejectionText
} from './incomeStatementAdapters'
import {
  getIncomeStatementComparison,
  getIncomeStatementStatus,
  getLastIncomeStatement,
  getPreviousIncomeStatement,
  requestIncomeStatementComparison,
  uploadIncomeStatement
} from '../../../../../actions/applicants'
import CreditCost from 'components/shared/CreditCost'
import { findCreditCost, sortDocsByDate } from 'tools/helper'
import { reportStyles } from '../shared/reportStyles'

const ACCEPTED_FILES = '.pdf,.jpg,.jpeg,.png,.csv,.xls,.xlsx'

const useStyles = makeStyles((theme) => ({
  ...reportStyles(theme),
  sidebar: {
    width: '100%'
  },
  content: {
    flex: 1,
    minWidth: 0
  },
  sidebarActions: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1.5)
  },
  periodItem: {
    flex: '1 1 220px',
    minWidth: 200,
    border: '1px solid #ECECEC',
    borderRadius: '10px',
    padding: theme.spacing(1.5),
    cursor: 'pointer',
    transition: 'border-color 120ms ease, background-color 120ms ease'
  },
  railButton: {
    borderRadius: '8px',
    padding: '10px 16px',
    fontWeight: 600,
    fontSize: 13,
    border: 'none',
    whiteSpace: 'nowrap',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6
  },
  railButtonSecondary: {
    backgroundColor: '#fff',
    color: '#333',
    border: '1px solid #ddd',
    cursor: 'pointer'
  },
  banner: {
    backgroundColor: BANNER_BG,
    borderRadius: '14px',
    padding: theme.spacing(4),
    color: '#fff',
    display: 'flex',
    justifyContent: 'space-between',
    gap: theme.spacing(3),
    marginBottom: theme.spacing(3),
    [theme.breakpoints.down('xs')]: {
      flexDirection: 'column'
    }
  },
  bannerChip: {
    fontSize: 10,
    fontWeight: 700,
    height: 20
  },
  tilesRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(3)
  },
  tileHead: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'baseline',
    gap: 8
  },
  tileDelta: {
    fontSize: 11,
    fontWeight: 600,
    color: 'var(--status-approved)',
    textAlign: 'right'
  },
  tileValue: {
    fontSize: 22,
    fontWeight: 700,
    margin: '6px 0 4px'
  },
  tileNote: {
    fontSize: 11,
    color: 'var(--neutral-400)'
  },
  barList: {
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
    paddingTop: 10,
    marginTop: 8,
    borderTop: '1px solid #F0F0F0'
  },
  barRow: {
    display: 'grid',
    gridTemplateColumns: '56px 1fr 64px',
    gap: 8,
    alignItems: 'center',
    fontSize: 11,
    color: 'var(--neutral-400)'
  },
  barTrack: {
    height: 6,
    backgroundColor: '#F3F3F3',
    borderRadius: 3,
    overflow: 'hidden'
  },
  barFill: {
    height: '100%',
    borderRadius: 3
  },
  sectionCard: {
    padding: theme.spacing(3),
    borderRadius: '12px',
    border: '1px solid #E8E8E8',
    boxShadow: 'none'
  },
  sectionTitle: {
    fontSize: 17,
    fontWeight: 700
  },
  sectionSubtitle: {
    fontSize: 12,
    color: 'var(--neutral-400)',
    marginTop: 2
  },
  twoColRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(3),
    marginBottom: theme.spacing(3)
  },
  colWide: {
    flex: '3 1 420px',
    minWidth: 320
  },
  colNarrow: {
    flex: '2 1 300px',
    minWidth: 280
  },
  colHalf: {
    flex: '1 1 300px',
    minWidth: 280
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: 13,
    minWidth: 440,
    marginTop: theme.spacing(2)
  },
  th: {
    padding: '8px',
    fontWeight: 700,
    fontSize: 10,
    letterSpacing: '0.08em',
    color: 'var(--neutral-400)',
    borderBottom: '1px solid #E8E8E8',
    textAlign: 'left'
  },
  td: {
    padding: '10px 8px',
    borderBottom: '1px solid #F0F0F0',
    verticalAlign: 'top'
  },
  right: {
    textAlign: 'right'
  },
  rowNote: {
    fontSize: 11,
    color: 'var(--neutral-400)'
  },
  typeChip: {
    display: 'inline-block',
    padding: '3px 8px',
    borderRadius: 4,
    fontSize: 11,
    fontWeight: 700,
    backgroundColor: '#F1F1F1',
    color: '#555'
  },
  severityChip: {
    display: 'inline-block',
    padding: '3px 8px',
    borderRadius: 4,
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: '0.04em',
    textTransform: 'uppercase'
  },
  flagGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
    columnGap: theme.spacing(3),
    marginTop: theme.spacing(1)
  },
  flagRow: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
    padding: `${theme.spacing(1.5)}px 0`,
    borderBottom: '1px solid #F0F0F0'
  },
  flagHead: {
    display: 'flex',
    gap: 10,
    alignItems: 'center',
    flexWrap: 'wrap'
  },
  checkRow: {
    display: 'flex',
    gap: 10,
    alignItems: 'flex-start',
    fontSize: 13,
    lineHeight: 1.5,
    padding: '6px 0'
  },
  checkIcon: {
    fontSize: 16,
    marginTop: 2,
    flexShrink: 0
  },
  checkDerived: {
    fontSize: 11,
    color: '#9a9a9a',
    marginLeft: 6
  },
  checkNote: {
    display: 'block',
    fontSize: 11,
    color: 'var(--neutral-400)',
    lineHeight: 1.5,
    marginTop: 2
  },
  lineAnnotation: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    fontSize: 11,
    color: 'var(--neutral-400)',
    fontWeight: 400
  },
  lineInfoIcon: {
    fontSize: 14,
    color: '#9a9a9a',
    cursor: 'help'
  },
}))

const CHECK_ICON_COLORS = {
  [CHECK_STATUS.PASS]: '#2c8f68',
  [CHECK_STATUS.DERIVED]: '#9a9a9a',
  [CHECK_STATUS.DOUBT]: ACCENT_PURPLE,
  [CHECK_STATUS.WARNING]: 'var(--status-pending)'
}

const CheckStatusIcon = ({ status, className }) => {
  const style = { color: CHECK_ICON_COLORS[status] || CHECK_ICON_COLORS[CHECK_STATUS.WARNING] }
  if (status === CHECK_STATUS.PASS || status === CHECK_STATUS.DERIVED) return <CheckIcon className={className} style={style} data-status={status} />
  if (status === CHECK_STATUS.DOUBT) return <InfoIcon className={className} style={style} data-status={status} />
  return <WarnIcon className={className} style={style} data-status={status} />
}

CheckStatusIcon.propTypes = {
  status: PropTypes.string.isRequired,
  className: PropTypes.string
}

// Next to a figure read from a canonical line: the amount the statement prints when the analysis uses another one
// (the note explains why, as a tooltip) and an info icon listing the normalizations behind the line.
const LineAnnotation = ({ line, classes }) => {
  if (!line) return null
  const hasReported = line.statementReports !== null
  const hasNormalizations = line.normalizations.length > 0
  if (!hasReported && !hasNormalizations) return null
  return (
    <span className={classes.lineAnnotation}>
      {hasReported && (
        <span title={line.note || undefined}>{strings.INCOME_STATEMENT_STATEMENT_REPORTS} {formatCurrency(line.statementReports)}</span>
      )}
      {hasNormalizations && (
        <span
          title={[strings.INCOME_STATEMENT_NORMALIZATIONS_TITLE, ...line.normalizations].join('\n')}
          style={{ display: 'inline-flex' }}
          data-line={line.name}
        >
          <InfoIcon className={classes.lineInfoIcon} />
        </span>
      )}
    </span>
  )
}

LineAnnotation.propTypes = {
  line: PropTypes.shape({
    name: PropTypes.string,
    note: PropTypes.string,
    statementReports: PropTypes.number,
    normalizations: PropTypes.arrayOf(PropTypes.string)
  }),
  classes: PropTypes.object.isRequired
}

const checkTooltip = (check) => (check.tolerance !== null ? `${strings.INCOME_STATEMENT_CHECK_TOLERANCE}${check.tolerance}` : undefined)

const MAX_BANNER_OBSERVATIONS = 3

// Which rail card is open: the current upload or the previous one. Each upload carries its most recent period only,
// as the balance sheet does; the other columns of the document only feed its verification and trends.
const CURRENT_SELECTION = { previous: false }
const PREVIOUS_SELECTION = { previous: true }

const IncomeStatement = ({ applicationId, creditCosts }) => {
  const classes = useStyles()

  const [initialLoading, setInitialLoading] = useState(true)
  const [status, setStatus] = useState('NOT_STARTED')
  const [ableToCompare, setAbleToCompare] = useState(false)
  const [comparisonStatus, setComparisonStatus] = useState('NOT_STARTED')
  const [currentDoc, setCurrentDoc] = useState(null)
  const [previousDoc, setPreviousDoc] = useState(null)
  const [uploading, setUploading] = useState(false)
  const [selection, setSelection] = useState(CURRENT_SELECTION)
  const [viewMode, setViewMode] = useState('report')
  const [comparisonData, setComparisonData] = useState(null)
  const [isLoadingComparison, setIsLoadingComparison] = useState(false)
  const [showConfirmDialog, setShowConfirmDialog] = useState(false)

  const documentComparisonCreditCost = () => findCreditCost(creditCosts, 'DOCUMENT_COMPARISON')
  const documentAnalysisCreditCost = () => findCreditCost(creditCosts, 'DOCUMENT_ANALYSIS')

  const fetchDocs = useCallback(async (hasPrevious) => {
    const current = await getLastIncomeStatement(applicationId)().catch(() => null)
    if (!hasPrevious) {
      setPreviousDoc(null)
      setCurrentDoc(current)
      return
    }
    const previous = await getPreviousIncomeStatement(applicationId)().catch(() => null)
    // Sort them by period end so that current is the latest and previous is the one before
    const [sortedPrevious, sortedCurrent] = sortDocsByDate(previous, current)
    setCurrentDoc(sortedCurrent)
    setPreviousDoc(sortedPrevious)
  }, [applicationId])

  const checkStatus = useCallback(async () => {
    try {
      const res = await getIncomeStatementStatus(applicationId)()
      const nextStatus = res?.status || 'NOT_STARTED'
      const canCompareNow = !!res?.ableToCompare
      setStatus(nextStatus)
      setAbleToCompare(canCompareNow)
      setComparisonStatus(res?.comparisonStatus || 'NOT_STARTED')
      if (nextStatus === 'COMPLETED') {
        await fetchDocs(canCompareNow)
      }
      return res
    } catch (err) {
      setStatus('NOT_STARTED')
      return null
    }
  }, [applicationId, fetchDocs])

  useEffect(() => {
    if (!applicationId) {
      setInitialLoading(false)
      return
    }

    checkStatus().finally(() => setInitialLoading(false))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applicationId])

  useEffect(() => {
    if (!applicationId || status !== 'IN_PROGRESS') return

    const interval = setInterval(async () => {
      try {
        const res = await getIncomeStatementStatus(applicationId)()
        const nextStatus = res?.status || 'NOT_STARTED'
        const canCompareNow = !!res?.ableToCompare
        setStatus(nextStatus)
        setAbleToCompare(canCompareNow)
        setComparisonStatus(res?.comparisonStatus || 'NOT_STARTED')
        if (nextStatus !== 'IN_PROGRESS') {
          clearInterval(interval)
          if (nextStatus === 'COMPLETED') fetchDocs(canCompareNow)
        }
      } catch (err) {
        clearInterval(interval)
      }
    }, 3000)

    return () => clearInterval(interval)
  }, [status, applicationId, fetchDocs])

  const handleUpload = async (event) => {
    const file = event.target.files[0]
    event.target.value = ''
    if (!file || !applicationId) return

    setUploading(true)
    try {
      await uploadIncomeStatement(applicationId, file)()
      Toast({ type: 'success', message: strings.INCOME_STATEMENT_UPLOAD_SUCCESS })
      await checkStatus()
    } catch (err) {
      Toast({ type: 'error', message: strings.INCOME_STATEMENT_UPLOAD_ERROR })
    } finally {
      setUploading(false)
    }
  }

  // Backend comparison between two uploads (costs credits)
  const canCompare = !!(currentDoc && previousDoc) && ableToCompare

  const handleCancelComparison = () => {
    setShowConfirmDialog(false)
  }

  const handleRequestComparison = async () => {
    if (!applicationId) return
    setShowConfirmDialog(false)
    setViewMode('comparison')
    if (comparisonData) return
    setIsLoadingComparison(true)
    try {
      // Read an already generated comparison when the status says there is one; otherwise generate it.
      let res = null
      if (comparisonStatus === 'COMPLETED') {
        res = await getIncomeStatementComparison(applicationId)().catch(() => null)
      }
      if (!res) {
        res = await requestIncomeStatementComparison(applicationId)()
      }
      setComparisonData(res)
    } catch (err) {
      Toast({ type: 'error', message: strings.INCOME_STATEMENT_COMPARISON_FAILED })
      setViewMode('report')
    } finally {
      setIsLoadingComparison(false)
    }
  }

  const handleClickComparison = () => {
    if (isLoadingComparison) return
    if (canCompare) {
      if (comparisonStatus === 'NOT_STARTED' && !comparisonData) {
        setViewMode('report')
        setShowConfirmDialog(true)
      } else {
        handleRequestComparison()
      }
    }
  }

  const activeDoc = selection.previous ? previousDoc : currentDoc
  // Deltas are read against the other upload
  const otherDoc = (selection.previous ? currentDoc : previousDoc) || null

  const metrics = useMemo(() => (activeDoc ? readMetrics(activeDoc) : null), [activeDoc])
  const otherMetrics = useMemo(() => (otherDoc ? readMetrics(otherDoc) : null), [otherDoc])
  const summary = useMemo(() => (metrics ? deriveSummary(metrics, otherMetrics) : null), [metrics, otherMetrics])
  const statusStyle = getStatusStyle(summary ? summary.statusLabel : null)
  const indicators = useMemo(() => (metrics ? deriveKeyIndicators(metrics, otherMetrics) : []), [metrics, otherMetrics])
  const costStructure = useMemo(() => (metrics ? deriveCostStructure(metrics) : null), [metrics])
  const flags = useMemo(() => (metrics ? deriveFlags(metrics) : []), [metrics])
  const checks = useMemo(() => (metrics ? deriveChecks(metrics) : []), [metrics])
  const bannerObservations = metrics ? metrics.observations.slice(0, MAX_BANNER_OBSERVATIONS) : []

  if (initialLoading) {
    return (
      <Box className={classes.container}>
        <Loader />
      </Box>
    )
  }

  if (uploading || status === 'IN_PROGRESS') {
    return (
      <Box className={classes.container}>
        <Box className={classes.centeredState}>
          <CircularProgress size={48} thickness={4} style={{ color: BANNER_BG, marginBottom: 24 }} />
          <Typography variant="h5" style={{ marginBottom: 16, fontWeight: 600 }}>
            {strings.INCOME_STATEMENT_PROCESSING_TITLE}
          </Typography>
          <Typography variant="body1" style={{ color: '#666' }}>
            {strings.INCOME_STATEMENT_PROCESSING_MESSAGE}
          </Typography>
        </Box>
      </Box>
    )
  }

  if (!currentDoc) {
    return (
      <Box className={classes.container}>
        {status === 'FAILED' && (
          <Alert severity="error" style={{ marginBottom: 16 }}>
            {strings.INCOME_STATEMENT_ANALYSIS_FAILED}
          </Alert>
        )}
        <input
          accept={ACCEPTED_FILES}
          style={{ display: 'none' }}
          id="income-statement-upload-empty"
          type="file"
          onChange={handleUpload}
        />
        <ReportEmptyState
          title={strings.INCOME_STATEMENT_NO_REPORTS_TITLE}
          description={strings.INCOME_STATEMENT_NO_REPORTS_MESSAGE}
          action={
            <Button
              size="large"
              style={{ padding: '12px 32px', fontSize: '1rem' }}
              loading={uploading}
              onClick={() => document.getElementById('income-statement-upload-empty')?.click()}
            >
              {strings.INCOME_STATEMENT_UPLOAD_DOCUMENT} <CreditCost cost={documentAnalysisCreditCost()} />
            </Button>
          }
        />
      </Box>
    )
  }

  const rail = (
    <IncomeStatementRail
      classes={classes}
      currentDoc={currentDoc}
      previousDoc={previousDoc}
      selection={selection}
      chip={deriveChecksChip(metrics)}
      onSelect={(nextSelection) => { setSelection(nextSelection); setViewMode('report') }}
      canCompare={canCompare}
      onCompareClick={handleClickComparison}
      onUpload={handleUpload}
      uploading={uploading}
      creditCosts={creditCosts}
    />
  )

  if (viewMode === 'comparison') {
    return (
      <div className={classes.container}>
        <div className={classes.layout}>
          {rail}
          <div className={classes.content}>
            {isLoadingComparison ? (
              <Box className={classes.centeredState}>
                <CircularProgress size={48} thickness={4} style={{ color: BANNER_BG, marginBottom: 24 }} />
                <Typography variant="h5" style={{ marginBottom: 16, fontWeight: 600 }}>
                  {strings.INCOME_STATEMENT_GENERATING_COMPARISON_TITLE}
                </Typography>
                <Typography variant="body1" style={{ color: '#666' }}>
                  {strings.INCOME_STATEMENT_ANALYZING_CHANGES}
                </Typography>
              </Box>
            ) : (
              <IncomeStatementComparison
                fromDoc={previousDoc}
                toDoc={currentDoc}
                comparisonData={comparisonData}
                onBack={() => setViewMode('report')}
              />
            )}
          </div>
        </div>
      </div>
    )
  }

  const rejected = !metrics.validIncomeStatement
  const noticeText = rejected ? [] : [metrics.error, ...metrics.warnings].filter(Boolean)
  // An incomplete statement is said once, with the doubts that make it so; the figures are still shown
  const incompleteText = !rejected && !metrics.statementComplete
    ? [strings.INCOME_STATEMENT_CHIP_INCOMPLETE + (metrics.confidence ? ` · ${strings.INCOME_STATEMENT_CONFIDENCE} ${metrics.confidence}` : ''), ...metrics.extractionDoubts]
    : []

  return (
    <div className={classes.container}>
      <div className={classes.layout}>
        {rail}

        <div className={classes.content}>
          {rejected && (
            <Alert severity="error" style={{ marginBottom: 16 }}>
              {rejectionText(metrics)}
            </Alert>
          )}
          {incompleteText.length > 0 && (
            <Alert severity="warning" style={{ marginBottom: 16 }}>
              {incompleteText.map((text) => (
                <div key={text}>{text}</div>
              ))}
            </Alert>
          )}
          {noticeText.length > 0 && (
            <Alert severity="warning" style={{ marginBottom: 16 }}>
              {noticeText.map((text) => (
                <div key={text}>{text}</div>
              ))}
            </Alert>
          )}

          {/* Verdict banner */}
          <div className={classes.banner}>
            <div>
              <Typography className={classes.bannerEyebrow} style={{ color: statusStyle.accent }}>
                {formatPeriodRange(metrics.periodStart, metrics.periodEnd).toUpperCase()} · {strings.INCOME_STATEMENT_VERDICT_LABEL}
              </Typography>
              <Typography className={classes.bannerHeadline}>{summary.headline}</Typography>
              <Typography className={classes.bannerDescription}>{summary.narrative}</Typography>
              {summary.bullets.length > 0 && (
                <ul className={classes.bannerBullets}>
                  {summary.bullets.map((bullet) => (
                    <li key={bullet}>{bullet}</li>
                  ))}
                </ul>
              )}
              {bannerObservations.length > 0 && (
                <>
                  <Typography className={classes.bannerObservationsHead}>{strings.INCOME_STATEMENT_OBSERVATIONS.toUpperCase()}</Typography>
                  <ul className={classes.bannerObservations}>
                    {bannerObservations.map((observation) => (
                      <li key={observation} className={classes.bannerObservation}>
                        <span className={classes.bannerObservationDot} />
                        <span>{observation}</span>
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </div>
            <div className={classes.bannerScoreCol}>
              <Typography className={classes.bannerScoreLabel}>{strings.INCOME_STATEMENT_HEALTH_SCORE}</Typography>
              <Typography className={classes.bannerScoreValue} style={{ color: statusStyle.accent }}>
                {metrics.healthScore.toFixed(1)}<span className={classes.bannerScoreUnit}>/10</span>
              </Typography>
              <Chip
                label={summary.statusLabel}
                size="small"
                className={classes.bannerChip}
                style={{ backgroundColor: statusStyle.chipBg, color: statusStyle.chipText }}
              />
              <div className={classes.dotStrip}>
                {Array.from({ length: 10 }).map((_, i) => (
                  <div
                    key={i}
                    className={classes.dot}
                    style={i < Math.round(metrics.healthScore) ? { backgroundColor: statusStyle.accent } : undefined}
                  />
                ))}
              </div>
              <Typography className={classes.bannerTrend}>{summary.statusText}</Typography>
            </div>
          </div>

          {/* Key indicators */}
          <div className={classes.tilesRow}>
            {indicators.map((kpi) => (
              <Paper className={classes.tile} key={kpi.key} style={kpi.negative ? { borderColor: 'var(--status-pending)' } : undefined}>
                <div className={classes.tileHead}>
                  <Typography className={classes.tileLabel}>{kpi.name.toUpperCase()}</Typography>
                  {kpi.delta && (
                    <Typography
                      className={classes.tileDelta}
                      style={kpi.delta.startsWith('-') || kpi.delta.startsWith(strings.INCOME_STATEMENT_KPI_DOWN_FROM) ? { color: 'var(--status-pending)' } : undefined}
                    >
                      {kpi.delta}
                    </Typography>
                  )}
                </div>
                <Typography className={classes.tileValue} style={kpi.negative ? { color: 'var(--status-pending)' } : undefined}>
                  {kpi.value}
                </Typography>
                <LineAnnotation line={kpi.line} classes={classes} />
                <Typography className={classes.tileNote}>{kpi.sub}</Typography>
                {kpi.bars.length > 0 && (
                  <div className={classes.barList}>
                    {kpi.bars.map((bar) => (
                      <div className={classes.barRow} key={bar.key}>
                        <span>{bar.label}</span>
                        <div className={classes.barTrack}>
                          <div
                            className={classes.barFill}
                            style={{ width: `${bar.pct}%`, backgroundColor: bar.negative ? 'var(--status-pending)' : (bar.last ? ACCENT_PURPLE : BANNER_BG) }}
                          />
                        </div>
                        <span style={{ textAlign: 'right', color: '#555' }}>{bar.text}</span>
                      </div>
                    ))}
                  </div>
                )}
              </Paper>
            ))}
          </div>

          {/* Cost structure + checks | flags */}
          <div className={classes.twoColRow} style={{ marginBottom: 0 }}>
            <div className={classes.colWide}>
              <Paper className={classes.sectionCard}>
                <div className={classes.sectionHead}>
                  <Typography className={classes.sectionTitle}>{strings.INCOME_STATEMENT_COST_STRUCTURE}</Typography>
                  {costStructure && (
                    <Typography className={classes.sectionSubtitle}>
                      {costStructure.periodLabel}, {formatShare(costStructure.total.pct)} {strings.INCOME_STATEMENT_COST_STRUCTURE_SUBTITLE_SUFFIX}
                    </Typography>
                  )}
                </div>
                {costStructure ? (
                  <div style={{ overflowX: 'auto' }}>
                    <table className={classes.table}>
                      <thead>
                        <tr>
                          <th className={classes.th} style={{ paddingLeft: 0 }}>{strings.INCOME_STATEMENT_COST_COLUMN_ITEM.toUpperCase()}</th>
                          <th className={classes.th}>{strings.INCOME_STATEMENT_COST_COLUMN_TYPE.toUpperCase()}</th>
                          {costStructure.months && <th className={`${classes.th} ${classes.right}`} title={costStructure.monthlyFormula}>{strings.INCOME_STATEMENT_COST_COLUMN_MONTHLY.toUpperCase()}</th>}
                          <th className={`${classes.th} ${classes.right}`}>{strings.INCOME_STATEMENT_COST_COLUMN_ANNUAL.toUpperCase()}</th>
                          <th className={`${classes.th} ${classes.right}`} style={{ paddingRight: 0 }}>{strings.INCOME_STATEMENT_COST_COLUMN_PCT.toUpperCase()}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {costStructure.rows.map((row) => (
                          <tr key={row.key}>
                            <td className={classes.td} style={{ paddingLeft: 0 }}>
                              <div style={{ fontWeight: 600 }}>{row.label}</div>
                              {row.note && <div className={classes.rowNote}>{row.note}</div>}
                              <LineAnnotation line={row.line} classes={classes} />
                            </td>
                            <td className={classes.td}>
                              <span
                                className={classes.typeChip}
                                style={row.debt ? { backgroundColor: BANNER_BG, color: '#fff' } : (row.income ? { backgroundColor: SEVERITY_STYLES.Positive.bg, color: SEVERITY_STYLES.Positive.fg } : undefined)}
                              >
                                {row.type}
                              </span>
                            </td>
                            {costStructure.months && <td className={`${classes.td} ${classes.right}`}>{formatOptionalCurrency(row.monthly)}</td>}
                            <td className={`${classes.td} ${classes.right}`}>{formatCurrency(row.value)}</td>
                            <td className={`${classes.td} ${classes.right}`} style={{ paddingRight: 0 }}>{formatShare(row.pct)}</td>
                          </tr>
                        ))}
                        <tr style={{ fontWeight: 700 }}>
                          <td className={classes.td} style={{ paddingLeft: 0, borderBottom: 0 }}>{strings.INCOME_STATEMENT_COST_TOTAL}</td>
                          <td className={classes.td} style={{ borderBottom: 0 }} />
                          {costStructure.months && <td className={`${classes.td} ${classes.right}`} style={{ borderBottom: 0 }}>{formatOptionalCurrency(costStructure.total.monthly)}</td>}
                          <td className={`${classes.td} ${classes.right}`} style={{ borderBottom: 0 }}>{formatCurrency(costStructure.total.value)}</td>
                          <td className={`${classes.td} ${classes.right}`} style={{ paddingRight: 0, borderBottom: 0 }}>{formatShare(costStructure.total.pct)}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <Typography className={classes.sectionSubtitle} style={{ marginTop: 16 }}>{strings.INCOME_STATEMENT_NO_REVENUE_BREAKDOWN}</Typography>
                )}
              </Paper>
              <Paper className={classes.sectionCard} style={{ marginTop: 24 }}>
                <Typography className={classes.sectionTitle}>{strings.INCOME_STATEMENT_CHECKS_TITLE}</Typography>
                <Box mt={1}>
                  {checks.map((check) => (
                    <div className={classes.checkRow} key={check.key}>
                      <CheckStatusIcon status={check.status} className={classes.checkIcon} />
                      <span style={{ minWidth: 0 }} title={checkTooltip(check)}>
                        <span>{check.label}</span>
                        {check.status === CHECK_STATUS.DERIVED && (
                          <span className={classes.checkDerived}>{strings.INCOME_STATEMENT_CHECK_DERIVED_SUFFIX}</span>
                        )}
                        {check.note && check.note !== strings.INCOME_STATEMENT_CHECK_NOT_VERIFIED && <span className={classes.checkNote}>{check.note}</span>}
                        {!check.verified && check.status === CHECK_STATUS.DERIVED && <span className={classes.checkNote}>{strings.INCOME_STATEMENT_CHECK_NOT_VERIFIED}</span>}
                      </span>
                    </div>
                  ))}
                </Box>
              </Paper>
            </div>

            <div className={classes.colNarrow}>
              <Paper className={classes.sectionCard}>
                <Typography className={classes.sectionTitle}>{strings.INCOME_STATEMENT_ANALYSIS_TITLE}</Typography>
                <Typography className={classes.sectionSubtitle}>{strings.INCOME_STATEMENT_ANALYSIS_SUBTITLE}</Typography>
                <div className={classes.flagGrid}>
                  {flags.length === 0 && <Typography className={classes.sectionSubtitle}>{strings.INCOME_STATEMENT_NO_ANALYSIS}</Typography>}
                  {flags.map((flag) => (
                    <div className={classes.flagRow} key={`${flag.severity}-${flag.line}`}>
                      <div className={classes.flagHead}>
                        <span
                          className={classes.severityChip}
                          style={{ backgroundColor: SEVERITY_STYLES[flag.severity].bg, color: SEVERITY_STYLES[flag.severity].fg }}
                        >
                          {flag.label}
                        </span>
                        <Typography className={classes.flagLine}>{flag.line}</Typography>
                      </div>
                    </div>
                  ))}
                </div>
              </Paper>
            </div>
          </div>

        </div>
      </div>

      {/* Confirmation Dialog for Comparison */}
      <Dialog
        open={showConfirmDialog}
        onClose={handleCancelComparison}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {strings.INCOME_STATEMENT_GENERATE_COMPARISON_TITLE}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            {strings.INCOME_STATEMENT_GENERATE_COMPARISON_MESSAGE}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCancelComparison}
            variant="outlined"
            color="secondary"
          >
            {strings.CANCEL}
          </Button>
          <Button
            onClick={handleRequestComparison}
            variant="contained"
            color="primary"
          >
            {strings.INCOME_STATEMENT_GENERATE_COMPARISON_TITLE}
            <CreditCost cost={documentComparisonCreditCost()} />
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  )
}

const IncomeStatementRail = ({
  classes,
  currentDoc,
  previousDoc,
  selection,
  chip,
  onSelect,
  canCompare,
  onCompareClick,
  onUpload,
  uploading,
  creditCosts
}) => {
  const periodsCount = 1 + (previousDoc ? 1 : 0)
  const documentAnalysisCreditCost = () => findCreditCost(creditCosts, 'DOCUMENT_ANALYSIS')

  const renderCard = ({ key, doc, title, subtitle, cardSelection }) => {
    const metrics = readMetrics(doc)
    const statusLabel = deriveSummary(metrics, null).statusLabel
    const statusStyle = getStatusStyle(statusLabel)
    const isActive = selection.previous === cardSelection.previous

    return (
      <div
        key={key}
        className={`${classes.periodItem} ${isActive ? classes.periodItemActive : ''}`}
        onClick={() => onSelect(cardSelection)}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography className={classes.periodDate}>{title}</Typography>
          <Typography className={classes.periodStatus} style={{ color: statusStyle.accent }}>{statusLabel}</Typography>
        </Box>
        {subtitle && <Typography className={classes.periodMeta}>{subtitle}</Typography>}
        <Typography className={classes.periodValue}>{formatOptionalCurrency(metrics.totalRevenue)}</Typography>
        <Typography className={classes.periodMeta}>
          {strings.INCOME_STATEMENT_TOTAL_REVENUE_SHORT} <LineAnnotation line={canonicalLine(metrics, 'revenue')} classes={classes} />
        </Typography>
        <Typography className={classes.periodMeta}>
          {strings.INCOME_STATEMENT_HEALTH} {metrics.healthScore.toFixed(1)} / 10 · {strings.INCOME_STATEMENT_NET_MARGIN} {formatOptionalShare(metrics.netMargin)}
          {' '}<LineAnnotation line={canonicalLine(metrics, 'netIncome')} classes={classes} />
        </Typography>
        <div className={classes.periodBar}>
          <div
            className={classes.periodBarFill}
            style={{ width: `${Math.min(100, (metrics.healthScore / 10) * 100)}%`, backgroundColor: statusStyle.accent }}
          />
        </div>
      </div>
    )
  }

  return (
    <div className={classes.sidebar}>
      <Paper className={classes.sidebarCard}>
        <div className={classes.sidebarHeader}>
          <div className={classes.sidebarHeaderLeft}>
            <Typography style={{ fontWeight: 700, fontSize: 16 }}>{strings.INCOME_STATEMENT_STATEMENTS}</Typography>
            <Typography className={classes.periodMeta}>{periodsCount} {strings.INCOME_STATEMENT_PERIODS_SUFFIX}</Typography>
            <Chip
              label={chip.label}
              size="small"
              className={classes.statusChip}
              style={{ backgroundColor: SEVERITY_STYLES[chip.tone].bg, color: SEVERITY_STYLES[chip.tone].fg }}
            />
          </div>
          <div className={classes.sidebarActions}>
            <button
              type="button"
              className={classes.railButton}
              disabled={!canCompare}
              onClick={onCompareClick}
              style={{
                backgroundColor: canCompare ? ACCENT_PURPLE : '#EDEBFB',
                color: canCompare ? '#fff' : '#b3a9f5',
                cursor: canCompare ? 'pointer' : 'not-allowed'
              }}
            >
              {strings.INCOME_STATEMENT_COMPARE_BUTTON}
            </button>

            <input
              accept={ACCEPTED_FILES}
              style={{ display: 'none' }}
              id="income-statement-upload"
              type="file"
              onChange={onUpload}
            />
            <label htmlFor="income-statement-upload">
              <span
                role="button"
                tabIndex={0}
                className={`${classes.railButton} ${classes.railButtonSecondary}`}
                style={{ cursor: uploading ? 'wait' : 'pointer', opacity: uploading ? 0.6 : 1 }}
              >
                <CloudUploadIcon style={{ fontSize: 16 }} />
                {uploading ? strings.INCOME_STATEMENT_UPLOADING : strings.INCOME_STATEMENT_UPLOAD_BUTTON} <CreditCost cost={documentAnalysisCreditCost()} />
              </span>
            </label>
          </div>
        </div>

        <div className={classes.periodsRow}>
          {previousDoc && renderCard({
            key: 'previous',
            doc: previousDoc,
            title: `${strings.INCOME_STATEMENT_PREVIOUS} · ${formatPeriodRange(previousDoc.periodStart, previousDoc.periodEnd || previousDoc.date)}`,
            cardSelection: PREVIOUS_SELECTION
          })}
          {renderCard({
            key: 'current',
            doc: currentDoc,
            title: `${strings.INCOME_STATEMENT_CURRENT} · ${formatPeriodRange(currentDoc.periodStart, currentDoc.periodEnd || currentDoc.date)}`,
            cardSelection: CURRENT_SELECTION
          })}
        </div>
      </Paper>
    </div>
  )
}

IncomeStatement.propTypes = {
  applicationId: PropTypes.string,
  creditCosts: PropTypes.array
}

IncomeStatementRail.propTypes = {
  classes: PropTypes.object.isRequired,
  currentDoc: PropTypes.object.isRequired,
  previousDoc: PropTypes.object,
  selection: PropTypes.shape({ previous: PropTypes.bool.isRequired }).isRequired,
  chip: PropTypes.shape({ tone: PropTypes.string.isRequired, label: PropTypes.string.isRequired }).isRequired,
  onSelect: PropTypes.func.isRequired,
  canCompare: PropTypes.bool.isRequired,
  onCompareClick: PropTypes.func.isRequired,
  onUpload: PropTypes.func.isRequired,
  uploading: PropTypes.bool.isRequired,
  creditCosts: PropTypes.array
}

export default IncomeStatement
